import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSection from '../containers/Banner';
import Navbar from '../containers/Navbar';
import WorkingProcessSection from '../containers/WorkingProcess';
import TextBoxSection from '../containers/TextBox';
import InfoSection from '../containers/Info';
import ComparisonTable from '../containers/ComparisonTable';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import FaqSection from '../containers/Faq';
import { useTranslation } from 'react-i18next';

const AppleIosPasswordManager = ({ title, description, pageContext }) => {
  const { t } = useTranslation();

  const iosFeatures = [
    {
      title: 'Native iOS Integration',
      alt: 'Native iOS Integration',
      description:
        'Seamlessly integrates with iOS AutoFill Password framework. Works natively with iOS 12 and above for secure password autofill across all apps and Safari.',
    },
    {
      title: 'Apple App Store Available',
      alt: 'Apple App Store Available',
      description:
        "Download directly from the Apple App Store with regular updates. Built to meet Apple's strict security, privacy, and quality requirements for iOS applications.",
    },
    {
      title: 'Face ID & Touch ID Support',
      alt: 'Face ID & Touch ID Support',
      description:
        "Unlock your password vault using Face ID or Touch ID. Enhanced security with Apple's secure biometric authentication integrated directly into the app.",
    },
    {
      title: 'iCloud Keychain Compatible',
      alt: 'iCloud Keychain Compatible',
      description:
        'Works alongside iCloud Keychain without conflicts. Import your existing iCloud passwords and manage everything securely with enhanced encryption in one place.',
    },
    {
      title: 'Cross-Device Apple Sync',
      alt: 'Cross-Device Apple Sync',
      description:
        'Sync passwords across all your Apple devices—iPhone, iPad, Mac, and Apple Watch. Your vault stays updated in real-time across the entire Apple ecosystem and beyond.',
    },
    {
      title: 'Privacy-First Architecture',
      alt: 'Privacy-First Architecture',
      description:
        'iOS users value privacy. Psono encrypts vault data client-side, and open-source code supports transparency.',
    },
  ];

  const iosFaqData = [
    {
      title: 'Is Psono a reliable password manager for iOS?',
      description:
        'Psono delivers professional-grade password management for iOS users at zero cost. The app is built on auditable open-source foundations, allowing security experts to verify its implementation. Organizations can self-host for complete data sovereignty. With NaCl encryption, seamless cross-device synchronization, and comprehensive free features, Psono provides a compelling alternative to subscription-based password managers for iPhone and iPad users.',
      expend: false,
    },
    {
      title: 'How do I set up Psono on iPhone or iPad?',
      description:
        'Create your Psono account at psono.pw and verify your email. Open the Apple App Store on your iOS device and search for "Psono Password Manager". Download and install the app, then launch it. Sign in with your credentials to sync your vault. Go to iOS Settings > Passwords > AutoFill Passwords and enable Psono to use it for automatic password filling across all apps and Safari.',
      expend: false,
    },
    {
      title: 'Which password manager is best for iPhone?',
      description:
        'For iPhone users seeking robust password management without recurring fees, Psono offers exceptional value. The app provides transparent open-source code that undergoes community security review. After installation from the App Store, Psono operates seamlessly—capturing credentials, generating strong passwords, and synchronizing your encrypted vault across all Apple devices and other platforms automatically.',
      expend: false,
    },
    {
      title: 'Can I export my passwords from Psono on iOS?',
      description:
        'Yes, Psono ensures complete data ownership through its export capability. While the export function is best accessed through the web interface, you can log into psono.pw from Safari on your iOS device, navigate to "Other" > "Export", choose your datastore and format (JSON or CSV), and download your password data.',
      expend: false,
    },
    {
      title: 'How do I import iCloud Keychain passwords to Psono?',
      description:
        'Currently, Apple does not provide a direct export feature for iCloud Keychain. However, you can manually add your most important passwords to Psono, or if you\'re using Safari on macOS, you can export passwords from Safari preferences and then import the CSV file into Psono\'s web interface using "Other" > "Import" > "Safari CSV" format.',
      expend: false,
    },
  ];

  const comparisonFeatures = [
    {
      title: 'Advanced Encryption',
      description:
        'Protect passwords with NaCl cryptography. Vault data is encrypted client-side before it is sent to the server.',
      psonoValue: true,
      iosValue: '-',
    },
    {
      title: 'Open Source & Auditable',
      description:
        'Publicly available source code allows security experts worldwide to verify security claims. Regular third-party audits ensure continued protection.',
      psonoValue: true,
      iosValue: '-',
    },
    {
      title: 'Advanced Two-Factor Authentication',
      description:
        'Secure your vault with TOTP authenticators, hardware security keys, or FIDO2 WebAuthn credentials for enhanced protection.',
      psonoValue: true,
      iosValue: '-',
    },
    {
      title: 'Password Generator',
      description:
        'Create long, complex, and unique passwords for every site with customizable options for maximum security.',
      psonoValue: true,
      iosValue: '-',
    },
    {
      title: 'Self-Hosting Option',
      description:
        'Deploy on your own infrastructure for complete control over your data and compliance with regulatory requirements.',
      psonoValue: true,
      iosValue: '-',
    },
    {
      title: 'Cross-Platform Sync',
      description:
        'Access your passwords on any browser, mobile device, or desktop OS with automatic synchronization across all devices.',
      psonoValue: true,
      iosValue: 'Limited',
    },
  ];

  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Best Free Password Manager iOS iPhone iPad | Psono')}
          lang={pageContext.lang}
          titleTemplate={`%s`}
          description={t(
            'Secure password manager app for iOS, iPhone, and iPad. Store unlimited passwords, sync across devices, and protect your data with military-grade encryption. Free open-source solution.'
          )}
          keywords={[
            'password manager ios',
            'password manager iphone',
            'password manager ipad',
            'best free password manager for ios',
            'ios password app',
            'iphone password vault',
            'secure password manager ios',
            'password manager app iphone',
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSection
            pageContext={pageContext}
            topText={t('Open-source password security for iOS')}
            titleText={t('Apple iOS Password Manager')}
            descriptionText={t(
              'Best free password manager for iPhone and iPad. Secure iOS password app with unlimited storage, Face ID/Touch ID support, and cross-device sync.'
            )}
            primeLink={
              'https://apps.apple.com/us/app/psono-password-manager/id1545581224'
            }
            primeText={t('INSTALL PSONO FOR iOS NOW')}
            downloadLink={`${pageContext.langPathPrefix}/download`}
            downloadText={t('VIEW ALL DOWNLOAD OPTIONS')}
          />
          <TextBoxSection
            description={t(
              'Professional password management for iPhone and iPad'
            )}
            text={t(
              'Psono delivers open-source password security designed specifically for iOS users. Access unlimited password storage, real-time synchronization across all platforms, and enterprise-grade security features—all included in a permanently free tier with native iOS integration, Face ID/Touch ID support, and seamless Apple ecosystem compatibility.'
            )}
          />
          <ComparisonTable
            features={comparisonFeatures.map((f) => ({
              ...f,
              chromeValue: f.iosValue,
            }))}
            browserName="iOS"
            title={t(
              'Top reasons to choose Psono over iOS native password manager'
            )}
          />
          <TextBoxSection
            description={t('Enhanced security beyond iOS defaults')}
            text={t(
              "iOS's built-in password features provide basic credential storage through iCloud Keychain, but enterprise-grade security demands more robust protection. True password security requires cryptographic uniqueness and sufficient complexity—requirements that manual password creation rarely meets. Psono generates provably random passwords using cryptographic algorithms for each service, implements NaCl encryption locally before any storage occurs, and offers configurable multi-factor authentication for vault protection. The iOS app delivers native AutoFill integration for rapid credential access while preserving encryption integrity during both storage operations and cross-device synchronization."
            )}
          />
          <WorkingProcessSection
            title={t('Why choose Psono as your iOS password manager?')}
            description={t(
              'Key features that make Psono stand out for iOS users'
            )}
            buttonUrl="/features-for-users"
            items={iosFeatures}
            pageContext={pageContext}
          />
          <TextBoxSection
            description={t('iOS installation and setup')}
            text={t(
              'Deploying Psono on iOS requires minimal setup time: Create your Psono account at psono.pw and complete email verification. Open the Apple App Store on your iPhone or iPad and install Psono Password Manager. Launch the app and sign in with your credentials. Enable Psono AutoFill in iOS Settings under Passwords. Configure Face ID or Touch ID for biometric access. Once active, Psono prompts you to store credentials securely as you log into apps and Safari. Subsequent logins enable instant autofill across all your iOS apps and websites. Your encrypted vault synchronizes continuously across iPhone, iPad, Mac, Apple Watch, and other platforms.'
            )}
          />
          <InfoSection
            pageContext={pageContext}
            titleContent={t('iOS App Security')}
            descriptionContent={t(
              "The Psono iOS app implements industry-standard NaCl cryptography for client-side encryption, securing vault data before network transmission. Our open-source codebase receives continuous evaluation from the international security community, providing transparency iOS users value. Face ID and Touch ID integration adds an extra layer of security using Apple's Secure Enclave. Whether protecting personal credentials or managing corporate authentication systems, Psono integrates proven encryption directly into your iOS device workflow."
            )}
          />
          <FaqSection data={iosFaqData} />
          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

AppleIosPasswordManager.propTypes = {
  title: PropTypes.object,
  description: PropTypes.object,
};

AppleIosPasswordManager.defaultProps = {
  title: {
    fontSize: ['24px', '32px', '40px', '42px', '46px'],
    fontWeight: '700',
    color: '#fff',
    letterSpacing: '-0.025em',
    mb: ['20px', '25px', '25px', '25px', '25px'],
    lineHeight: '1.2',
    textAlign: 'center',
  },
  description: {
    fontSize: ['15px', '16px', '16px', '16px', '16px'],
    color: '#fff',
    lineHeight: '1.75',
    mb: '0',
    textAlign: 'center',
  },
};

export default AppleIosPasswordManager;
